
import { BrowserRouter as Router, Routes, Route, useParams } from 'react-router-dom';
import { ReactNode, useEffect, useState } from 'react';
import Checkout from '@pages/checkout';
import Thanks from '@pages/thanks';
import CheckoutSkeletonView from '@pages/checkout/views/CheckoutSkeletonView';
import CommunityNotFoundView from '@pages/checkout/views/CommunityNotFoundView';

import '@styles/index.scss';

import api from '@services/api';
import pixel from '@services/pixel';

import payload from '@app/payload';
import { t } from '@i18n';

const appearanceValues = ['light', 'dark', 'system'] as const;
type AppearancePreference = typeof appearanceValues[number];
type CheckoutMode = 'loading' | 'ready' | 'community-not-found';

function isAppearancePreference(value: string | null): value is AppearancePreference {
  return appearanceValues.includes(value as AppearancePreference);
}

function CheckoutWrapper({ pageComponent }: { pageComponent: ReactNode }) {
  const { checkoutSlug } = useParams();
  const [mode, setMode] = useState<CheckoutMode>('loading');

  useEffect(() => {
    let mounted = true;
    setMode('loading');

    const initializeApp = async () => {
      try {
        await api.configure();

        const nextMode = await initializePayload(checkoutSlug);

        if (!mounted) {
          return;
        }

        setMode(nextMode);

        if (nextMode === 'ready') {
          pixel.initialize(payload.pixels || []);
          setTimeout(() => {
            pixel.dispatchEvent(pixel.events.page_view);
          }, 150);
        }
      } catch (error) {
        console.error(t('app.init.error'), error);
      }
    };

    initializeApp();

    return () => {
      mounted = false;
    };
  }, [checkoutSlug]);

  if (mode === 'loading') {
    return <CheckoutSkeletonView />;
  }

  if (mode === 'community-not-found') {
    return <CommunityNotFoundView />;
  }

  return <>{pageComponent}</>;
}

function App() {
  useEffect(() => {
    const appearanceKey = 'system_appearence';
    const mediaQuery = window.matchMedia?.('(prefers-color-scheme: dark)');

    const resolveTheme = (appearance: AppearancePreference) => {
      if (appearance === 'system') {
        return mediaQuery?.matches ? 'dark' : 'light';
      }

      return appearance === 'dark' ? 'dark' : 'light';
    };

    const readAppearance = () => {
      const params = new URLSearchParams(window.location.search);
      const urlAppearance = params.get('theme') || params.get('appearance');

      if (isAppearancePreference(urlAppearance)) {
        localStorage.setItem(appearanceKey, urlAppearance);
        return urlAppearance;
      }

      const savedAppearance = localStorage.getItem(appearanceKey);

      return isAppearancePreference(savedAppearance) ? savedAppearance : 'light';
    };

    const applyAppearance = (appearance: AppearancePreference = readAppearance()) => {
      const theme = resolveTheme(appearance);

      document.body.classList.remove('light', 'dark', 'light-mode', 'dark-mode');
      document.body.classList.add(theme, `${theme}-mode`);
      window.appearenceMode = theme;
      window.dispatchEvent(new CustomEvent('appearence-change', { detail: theme }));

      return theme;
    };

    window.triggerAppearence = (value) => {
      const savedCurrent = localStorage.getItem(appearanceKey);
      const current = isAppearancePreference(savedCurrent) ? savedCurrent : 'light';
      const cycle: AppearancePreference[] = ['light', 'system', 'dark'];
      const next = isAppearancePreference(value || null)
        ? value
        : cycle[(cycle.indexOf(current) + 1) % cycle.length];

      localStorage.setItem(appearanceKey, next);
      return applyAppearance(next);
    };

    applyAppearance();

    const handleSystemThemeChange = () => {
      if ((localStorage.getItem(appearanceKey) || 'light') === 'system') {
        applyAppearance('system');
      }
    };

    mediaQuery?.addEventListener('change', handleSystemThemeChange);

    return () => mediaQuery?.removeEventListener('change', handleSystemThemeChange);
  }, []);

  return (
    <Router>
      <div>
        <Routes>
          <Route path="/" element={<CheckoutWrapper pageComponent={<Checkout />} />} />
          <Route path="/checkout" element={<CheckoutWrapper pageComponent={<Checkout />} />} />
          <Route path="/:checkoutSlug" element={<CheckoutWrapper pageComponent={<Checkout />} />} />
          <Route path="/:checkoutSlug/thanks" element={<CheckoutWrapper pageComponent={<Thanks />} />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;



async function initializePayload(checkoutSlug?: string): Promise<CheckoutMode> {
  const slug = String(checkoutSlug || '').trim();

  if (!slug) {
    return 'community-not-found';
  }

  const response: any = await api.get(`checkout/product?slug=${encodeURIComponent(slug)}`);

  if (response?.status === 404 || response?.response?.status === 404) {
    return 'community-not-found';
  }

  if (!response?.data) {
    return 'community-not-found';
  }

  Object.assign(payload, response.data);
  return 'ready';
}
